import mixpanel from "mixpanel-browser";

import { getStoredShortLinkContext } from "@punchup/utils/shortlink";
import { getStoredUserRoles, UserRoleProperties } from "./user-roles";

/**
 * A snapshot of browser-side attribution state — referrer, page URL, utm_*
 * params, stored short-link context, and the role super-props — collected
 * client-side and shipped in the request body of API calls whose analytics
 * events are tracked server-side.
 *
 * Why this exists: server-side Mixpanel tracking (`logServerEvent`) sends
 * only the props it is explicitly given — none of the defaults
 * `mixpanel-browser` auto-attaches ($referrer, $referring_domain,
 * $current_url, utm_*), and none of the context our client `trackEvent`
 * wrapper merges (short-link props, role super-props). Any event moved from
 * browser tracking to server tracking silently loses that attribution unless
 * the client passes this context along with the request.
 */
export interface WebAttributionContext {
  /** `document.referrer` at the time of the action ("" for direct traffic) */
  referrer?: string;
  /**
   * First-touch referrer super-props mixpanel-browser persists via
   * `register_once` at init (`$initial_referrer` / `$initial_referring_domain`,
   * defaulting to "$direct"). These are what the follow-attribution pipeline
   * reads — `comedian-weekly-digest/fetch-follow-events.ts` and the
   * `events_followed_comedian` export expose ONLY `$initial_*`, not the
   * last-touch `$referrer`/`$referring_domain` above — so a server-tracked
   * follow must carry them for that metric to survive the client→server move.
   */
  initialReferrer?: string;
  initialReferringDomain?: string;
  /** `window.location.href` at the time of the action */
  currentUrl?: string;
  /** utm_* params present on the current URL */
  utmParams?: Record<string, string>;
  /** Short-link attribution from session storage (see @punchup/utils/shortlink) */
  shortCode?: string;
  southpawJobId?: string;
  attribution?: Record<string, string>;
  /** Role super-props (attached to every browser event — see ./user-roles) */
  userRoles?: UserRoleProperties;
}

const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
] as const;

/**
 * Collect the browser's current attribution state. Returns undefined on the
 * server, where there is no browser state to collect.
 *
 * `document.referrer` survives client-side (SPA) navigation, so at
 * follow/action time it still holds the external site the user arrived from —
 * the same value mixpanel-browser would have attached as $referrer.
 *
 * Fail-open: this runs inline before the action's API call, so it must never
 * throw — attribution is best-effort, the action is not.
 */
export function collectWebAttributionContext():
  | WebAttributionContext
  | undefined {
  if (typeof window === "undefined") {
    return undefined;
  }

  try {
    const utmParams: Record<string, string> = {};
    const search = new URLSearchParams(window.location.search);
    for (const key of UTM_KEYS) {
      const value = search.get(key);
      if (value) utmParams[key] = value;
    }

    let shortLinkContext = null;
    try {
      shortLinkContext = getStoredShortLinkContext();
    } catch {
      // sessionStorage access throws SecurityError when storage is blocked
      // (e.g. "block all cookies") — keep the referrer/URL part of the
      // snapshot rather than losing all of it.
    }

    // First-touch referrer, read back from mixpanel-browser's own persistence.
    // mixpanel registers $initial_referrer / $initial_referring_domain once at
    // init and stamped them on every client-tracked event; passing the exact
    // stored values through means the server-tracked follow carries the same
    // first-touch attribution the pipeline reads (no recomputation, so it also
    // matches mixpanel's referring-domain derivation exactly).
    let initialReferrer: string | undefined;
    let initialReferringDomain: string | undefined;
    try {
      const storedInitialReferrer = mixpanel.get_property("$initial_referrer");
      const storedInitialReferringDomain = mixpanel.get_property(
        "$initial_referring_domain"
      );
      if (typeof storedInitialReferrer === "string") {
        initialReferrer = storedInitialReferrer;
      }
      if (typeof storedInitialReferringDomain === "string") {
        initialReferringDomain = storedInitialReferringDomain;
      }
    } catch {
      // mixpanel not initialised yet / persistence blocked — omit first-touch,
      // keep the rest of the snapshot.
    }

    return {
      referrer: document.referrer,
      ...(initialReferrer ? { initialReferrer } : {}),
      ...(initialReferringDomain ? { initialReferringDomain } : {}),
      currentUrl: window.location.href,
      ...(Object.keys(utmParams).length > 0 ? { utmParams } : {}),
      ...(shortLinkContext
        ? {
            shortCode: shortLinkContext.shortCode,
            southpawJobId: shortLinkContext.southpawJobId,
            attribution: shortLinkContext.attribution,
          }
        : {}),
      userRoles: getStoredUserRoles(),
    };
  } catch {
    return undefined;
  }
}

const MAX_VALUE_LENGTH = 1000;
// Mixpanel rejects events whose property NAMES exceed 255 chars, so keys get
// a tighter bound than values.
const MAX_KEY_LENGTH = 255;
const MAX_ATTRIBUTION_ENTRIES = 25;

const ROLE_KEYS = [
  "is_super_admin",
  "is_venue_manager",
  "is_comedian_manager",
] as const;

function asBoundedString(value: unknown): string | undefined {
  return typeof value === "string" && value.length > 0
    ? value.slice(0, MAX_VALUE_LENGTH)
    : undefined;
}

/**
 * Derive the referring domain the way mixpanel-browser does: the referrer's
 * hostname, or the literal "$direct" when there is no (or an unparseable)
 * referrer. Keeping the "$direct" convention means direct traffic stays
 * distinguishable from events that carry no attribution context at all
 * (server-initiated actions, mobile app).
 */
function referringDomain(referrer: string | undefined): string {
  if (!referrer) return "$direct";
  try {
    return new URL(referrer).hostname || "$direct";
  } catch {
    return "$direct";
  }
}

/**
 * Convert a client-supplied {@link WebAttributionContext} into the props
 * browser-side tracking would have attached itself.
 *
 * The input arrives as untrusted request JSON, so this is defensive: anything
 * malformed degrades to {} rather than throwing, keys and values are bounded,
 * and every top-level field is allowlisted. `attribution` entries carry
 * arbitrary keys (parity with client `trackEvent`, which spreads the same
 * dict) but are spread first, so they can never override the $-props, the
 * utm/short-link/role keys, or the caller's own core event props.
 */
export function webAttributionContextToMixpanelProps(
  context: unknown
): Record<string, string | boolean> {
  if (
    typeof context !== "object" ||
    context === null ||
    Array.isArray(context)
  ) {
    return {};
  }
  const ctx = context as Record<string, unknown>;
  const props: Record<string, string | boolean> = {};

  if (
    typeof ctx.attribution === "object" &&
    ctx.attribution !== null &&
    !Array.isArray(ctx.attribution)
  ) {
    for (const [key, value] of Object.entries(ctx.attribution).slice(
      0,
      MAX_ATTRIBUTION_ENTRIES
    )) {
      const bounded = asBoundedString(value);
      if (bounded) props[key.slice(0, MAX_KEY_LENGTH)] = bounded;
    }
  }

  if (typeof ctx.utmParams === "object" && ctx.utmParams !== null) {
    const utm = ctx.utmParams as Record<string, unknown>;
    for (const key of UTM_KEYS) {
      const bounded = asBoundedString(utm[key]);
      if (bounded) props[key] = bounded;
    }
  }

  const shortCode = asBoundedString(ctx.shortCode);
  if (shortCode) props.short_code = shortCode;

  const southpawJobId = asBoundedString(ctx.southpawJobId);
  if (southpawJobId) props.southpaw_job_id = southpawJobId;

  const currentUrl = asBoundedString(ctx.currentUrl);
  if (currentUrl) props.$current_url = currentUrl;

  const referrer = asBoundedString(ctx.referrer);
  if (referrer) props.$referrer = referrer;
  props.$referring_domain = referringDomain(referrer);

  // First-touch props, passed through verbatim from mixpanel-browser's stored
  // values (already "$direct" for direct traffic). The follow-attribution
  // pipeline keys on these, so restoring them is what un-breaks the metric that
  // cliffed when follow tracking moved server-side.
  const initialReferrer = asBoundedString(ctx.initialReferrer);
  if (initialReferrer) props.$initial_referrer = initialReferrer;

  const initialReferringDomain = asBoundedString(ctx.initialReferringDomain);
  if (initialReferringDomain) {
    props.$initial_referring_domain = initialReferringDomain;
  }

  // Role super-props: always present as real booleans on every browser event
  // (the user-roles contract), so a valid context always emits all three —
  // an absent/malformed snapshot means false, exactly like the client default.
  const roles =
    typeof ctx.userRoles === "object" && ctx.userRoles !== null
      ? (ctx.userRoles as Record<string, unknown>)
      : undefined;
  for (const key of ROLE_KEYS) props[key] = roles?.[key] === true;

  return props;
}
